import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { touchVariants } from "~/lib/motion-obj";
import { cn } from "~/lib/utils";
import ScrollArrowButton from "./ScrollArrowButton";
import { useFontStyle } from "~/hook/useFontStyle";

type ChipPropsType = {
  chipData: (number | string)[];
  selectedValue: number | string;
  onClick: (value: number | string) => void;
  className?: string;
  extraChip?: string;
  justify?: string;
};

const Chip: React.FC<ChipPropsType> = ({ chipData, selectedValue, className, onClick, extraChip, justify }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());
  const { medium } = useFontStyle();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 50);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 300;
      const value = direction === "left" ? -scrollAmount : scrollAmount;
      container.scrollBy({ left: value, behavior: "smooth" });
    }
  };

  const isCheckIsNumber = (item: number | string, value: number | string) => {
    if (!value) return false;
    if (typeof value === "number") return item === value;
    return item.toString() === value.toString();
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    checkScroll();

    const handleResize = () => checkScroll();
    const handleScroll = () => checkScroll();

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // // Scroll selected chip into view when it changes
  useEffect(() => {
    const el = chipRefs.current.get(selectedValue);
    if (el && scrollRef.current) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedValue]);

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <div className={cn("w-full h-full flex items-center gap-[1%] justify-between px-2", className || "")}>
      <div className={cn("w-[2%] py-4  ustify-center items-center z-10", canScrollLeft ? "hidden lg:flex" : "hidden")}>
        <ScrollArrowButton direction="left" onClick={() => scroll("left")} disabled={!canScrollLeft} />
      </div>

      <div
        ref={scrollRef}
        className={cn("flex gap-4 overflow-x-auto scrollbar-hide w-full rounded-full", showArrows ? "justify-start" : `justify-center ${justify}`)}
      >
        {extraChip && (
          <div ref={(el) => el && chipRefs.current.set(0, el)}>
            <motion.div
              variants={touchVariants}
              initial="initial"
              whileTap="tap"
              onClick={() => onClick(0)}
              className={cn(
                "px-4 py-2 h-9 text-16 rounded-full flex justify-center items-center gap-3 cursor-pointer text-nowrap font-normal",
                medium,
                isCheckIsNumber(0, selectedValue) ? "bg-chipSelectedColor text-white" : "bg-secondaryColor hover:bg-chipHoverColor text-textColor",
              )}
            >
              {extraChip}
            </motion.div>
          </div>
        )}

        {chipData.map((item) => (
          <div key={item} ref={(el) => el && chipRefs.current.set(item, el)}>
            <motion.div
              variants={touchVariants}
              initial="initial"
              whileTap="tap"
              onClick={() => onClick(item)}
              className={cn(
                "px-4 py-1 h-9 text-16 rounded-full flex justify-center items-center gap-3 cursor-pointer text-nowrap font-normal",
                medium,
                isCheckIsNumber(item, selectedValue) ? "bg-chipSelectedColor text-white" : "bg-secondaryColor hover:bg-chipHoverColor text-textColor",
              )}
            >
              {/* mt-1  MokuBrush font apply */}
              <span className="body_text_m  capitalize">{item}</span>
            </motion.div>
          </div>
        ))}
      </div>

      <div className={cn("w-[2%] py-4  justify-center items-center z-10", canScrollRight ? "hidden lg:flex" : "hidden")}>
        <ScrollArrowButton direction="right" onClick={() => scroll("right")} disabled={!canScrollRight} />
      </div>
    </div>
  );
};

export default Chip;
